import { useCallback, useState } from 'react';
import { Button, Flex } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { TextInput as RhfTextInput } from 'react-hook-form-mantine';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { formatPhoneNumber } from '@dealstack/shared/lib/format/phoneNumber';
import { PhoneNumberInput } from '@dealstack/frontend/shared/components/forms/PhoneNumberInput/PhoneNumberInput';
import { OtpFormValues } from '../EidEasySigningModal.types';
import { isValidContact, resolveInitialChannel } from '../EidEasySigningModal.utils';
import { StepBody } from './StepBody';
import { StepFooter } from './StepFooter';
import { StepHeader } from './StepHeader';
import { StepHeading, StepSubHeading } from './typography';
import { SignerContactType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	onConfirm: (channel: SignerContactType, value: string) => void;
	onGoBack: () => void;
	defaultPhone?: string;
	defaultPhonePrefix?: string;
	defaultEmail?: string;
	hideStepHeader?: boolean;
}

export const OtpStep = ({
	onConfirm,
	onGoBack,
	defaultPhone = '',
	defaultPhonePrefix = '',
	defaultEmail = '',
	hideStepHeader,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const [otpChannel, setOtpChannel] = useState<SignerContactType>(() =>
		resolveInitialChannel(defaultPhone, defaultEmail)
	);

	const otpForm = useForm<OtpFormValues>({
		defaultValues: {
			phoneNumber: formatPhoneNumber(defaultPhonePrefix, defaultPhone),
			telephonePrefix: defaultPhonePrefix,
			email: defaultEmail,
		},
	});

	const phoneFullValue = otpForm.watch('phoneNumber') ?? '';
	const emailValue = otpForm.watch('email') ?? '';

	const handleConfirm = useCallback(() => {
		const value = otpChannel === SignerContactType.SMS ? phoneFullValue : emailValue;
		if (!isValidContact(value, otpChannel)) {
			return;
		}
		onConfirm(otpChannel, value);
	}, [otpChannel, phoneFullValue, emailValue, onConfirm]);

	const canSubmit = isValidContact(
		otpChannel === SignerContactType.SMS ? phoneFullValue : emailValue,
		otpChannel
	);

	return (
		<FormProvider {...otpForm}>
			<Flex direction="column" w="100%" h="100%">
				{!hideStepHeader && <StepHeader onGoBack={onGoBack} />}
				<StepBody>
					<Flex direction="column" gap={4}>
						<StepHeading>{tCommonUi('eid_easy_signing.otp_title')}</StepHeading>
						<StepSubHeading>
							{otpChannel === SignerContactType.SMS
								? tCommonUi('eid_easy_signing.otp_phone_subtitle')
								: tCommonUi('eid_easy_signing.otp_email_subtitle')}
						</StepSubHeading>
					</Flex>
					{otpChannel === SignerContactType.SMS ? (
						<PhoneNumberInput
							name="phoneNumber"
							phonePrefixName="telephonePrefix"
							label={tCommonUi('eid_easy_signing.otp_phone_label')}
							size="small"
						/>
					) : (
						<RhfTextInput
							name="email"
							control={otpForm.control}
							label={tCommonUi('eid_easy_signing.otp_email_label')}
							type="email"
							autoFocus
							data-testid={TEST_ELEMENT_IDS.EID_EASY_OTP_EMAIL_INPUT}
						/>
					)}
					{otpChannel === SignerContactType.SMS && emailValue && (
						<Button
							variant="subtle"
							size="compact-sm"
							onClick={() => setOtpChannel(SignerContactType.EMAIL)}
							style={{ alignSelf: 'flex-start' }}
							data-testid={TEST_ELEMENT_IDS.EID_EASY_OTP_USE_EMAIL_BUTTON}
						>
							{tCommonUi('eid_easy_signing.otp_use_email_instead')}
						</Button>
					)}
					{otpChannel === SignerContactType.EMAIL && phoneFullValue && (
						<Button
							variant="subtle"
							size="compact-sm"
							onClick={() => setOtpChannel(SignerContactType.SMS)}
							style={{ alignSelf: 'flex-start' }}
						>
							{tCommonUi('eid_easy_signing.otp_use_phone_instead')}
						</Button>
					)}
				</StepBody>
				<StepFooter>
					<Button
						variant="filled"
						fullWidth
						disabled={!canSubmit}
						onClick={handleConfirm}
						data-testid={TEST_ELEMENT_IDS.EID_EASY_OTP_SEND_CODE_BUTTON}
					>
						{tCommonUi('eid_easy_signing.otp_send_code')}
					</Button>
				</StepFooter>
			</Flex>
		</FormProvider>
	);
};
